import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components' // Import styled-components
import { TextField } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import {
  getBorderRadius,
  getTextfieldFontSize,
  getTextfieldLabelFontSize,
  getColorVariant,
  getTextfieldLabelTransform,
  getTextfieldHeight,
} from 'src/components/core/functions'

// Base TextField styles using styled-components
const BaseTextField = styled(TextField)`
  .MuiInputBase-root {
    font-family: ${({ fontFamily }) => fontFamily};
    font-size: ${({ size }) => getTextfieldFontSize(size)};
    //height: ${({ size }) => getTextfieldHeight(size)};
    background-color: ${({ theme }) => theme.palette.white.main};
    border-radius: 8px;
    overflow: ${({ $maxHeight }) => ($maxHeight ? 'visible' : 'hidden')};
  }

  .MuiInputBase-inputMultiline {
    max-height: ${({ $maxHeight }) => $maxHeight || 'none'} !important;
    overflow-y: ${({ $maxHeight }) => ($maxHeight ? 'auto' : 'visible')} !important;
  }

  .MuiInputLabel-root {
    font-size: ${({ size }) => getTextfieldLabelFontSize(size)};
    line-height: 1.2em;
    color: ${({ theme, disabled }) =>
      disabled ? theme.palette.disabled.dark : theme.palette.dark.main};
    font-family: ${({ fontFamily }) => fontFamily};
    transform: ${({ size }) => getTextfieldLabelTransform(size)};

    &.Mui-focused {
      transform: translate(0px, -20px) scale(1) !important;
      color: ${({ theme, color }) => theme.palette.secondary.main};
    }
    &.MuiInputLabel-shrink {
      transform: translate(0px, -20px) scale(1) !important;
      color: ${({ theme, color }) => theme.palette.secondary.main};
    }
  }

  .MuiInputBase-input {
    color: ${({ theme }) => theme.palette.secondary.main};
    background-color: #fff;
  }

  .MuiFormHelperText-root {
    font-size: 0.75rem;
    margin-top: 4px;
    color: ${({ theme, color, error }) =>
      error ? theme.palette.error.main : getColorVariant(theme, color).main};
  }

  &.Mui-disabled {
    .MuiInputBase-root {
      background-color: ${({ theme }) =>
        theme.palette.action.disabledBackground};
    }
  }
`

// Variant-specific styles for Outlined TextField
const OutlinedTextField = styled(BaseTextField)`
  margin-top: ${({ $marginTop }) => ($marginTop ? '20px' : '0px')};
  .MuiOutlinedInput-root {
    & fieldset {
      top: 0px;
      border-color: ${({ theme }) => theme.palette.dark.main};
      & legend {
        display: none;
      }
    }
    &:hover fieldset {
      border-color: ${({ theme, color }) => getColorVariant(theme, color).main};
    }
    &.Mui-focused fieldset {
      border-color: ${({ theme, color }) => getColorVariant(theme, color).main};
    }

    &.Mui-error fieldset {
      border-color: ${({ theme }) => theme.palette.error.main};
    }
    &.Mui-disabled fieldset {
      border-color: ${({ theme }) => theme.palette.action.disabledBackground};
      background-color: ${({ theme }) =>
        theme.palette.action.disabledBackground};
    }
  }
`

// Variant-specific styles for Filled TextField
const FilledTextField = styled(BaseTextField)`
  .MuiFilledInput-root {
    & fieldset {
      top: 0px;
      & legend {
        display: none;
      }
    }
    background-color: ${({ theme }) => theme.palette.action.hover};
    &:hover {
      background-color: ${({ theme }) => theme.palette.action.hover};
    }
    &.Mui-focused {
      background-color: ${({ theme }) => theme.palette.action.selected};
    }
    &.Mui-error {
      background-color: ${({ theme }) => theme.palette.error.light};
    }
    &.Mui-disabled {
      background-color: ${({ theme }) =>
        theme.palette.action.disabledBackground};
    }
  }
`

// Variant-specific styles for Standard TextField
const StandardTextField = styled(BaseTextField)`
  .MuiInput-underline {
    & fieldset {
      top: 0px;
      & legend {
        display: none;
      }
    }
    &::after {
      border-bottom: 0px;
    }
    &::before {
      border-bottom: 0px;
    }
    &:hover::before {
      border-bottom: 0px;
    }
    &.Mui-focused::before {
      border-bottom: 0px;
    }
    &.Mui-error::before {
      border-bottom: 0px;
    }
    &.Mui-disabled::before {
      border-bottom: 0px;
    }
  }
`

// CoreTextField component with forwardRef
const CoreTextField = forwardRef(
  (
    {
      variant = 'outlined',
      size = 'medium',
      color = 'primary', // Add color prop with default 'primary'
      fullWidth = false,
      error = false,
      disabled = false,
      helperText,
      label,
      marginTop = true,
      multiline = false,
      fontFamily = 'Inter',
      autoComplete = 'off',
      maxHeight,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme() // Access the theme using the useTheme hook

    // Select the TextField variant based on the `variant` prop
    const TextFieldVariant =
      {
        outlined: OutlinedTextField,
        filled: FilledTextField,
        standard: StandardTextField,
      }[variant] || OutlinedTextField

    return (
      <TextFieldVariant
        ref={ref}
        theme={theme}
        variant={variant}
        size={size}
        color={color}
        fullWidth={fullWidth}
        error={error}
        disabled={disabled}
        helperText={helperText}
        label={label}
        $marginTop={marginTop}
        $maxHeight={maxHeight}
        multiline={multiline}
        fontFamily={fontFamily}
        autoComplete={autoComplete}
        {...props}
      />
    )
  },
)

// Prop validation
CoreTextField.propTypes = {
  variant: PropTypes.oneOf(['outlined', 'filled', 'standard']),
  size: PropTypes.oneOf(['small', 'medium']),
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'error',
    'info',
    'success',
    'warning',
    'dark',
    'inherit',
  ]), // Add color prop validation
  fullWidth: PropTypes.bool,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
  label: PropTypes.string,
  marginTop: PropTypes.bool,
  fontFamily: PropTypes.string,
  maxHeight: PropTypes.string,
}

export default CoreTextField
